import { useLayoutEffect, useRef, useState } from 'react';

// Classe do <html> com o calendário maximizado: a página de trás não rola (style.css)
export const MAXIMIZED_PAGE_CLASS = 'agenda-calendar-maximized-open';

// Janelas que abrem por cima do calendário maximizado: o modal do agendamento (react-bootstrap), o Filtros (Offcanvas) e a
// pergunta do SweetAlert. Com uma delas na tela, o Esc e o Tab ficam com ela. O toast do SweetAlert (os avisos) não conta:
// ele não usa o teclado, e o Esc não o fecha
const OVERLAY = '.modal.show, .offcanvas.show, .swal2-popup.swal2-modal';
const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
// Sem o checkVisibility (navegador antigo, jsdom), conta como visível
const isVisible = (element) => (typeof element.checkVisibility === 'function' ? element.checkVisibility() : true);

// Volta a página para a rolagem de antes de uma vez: o Bootstrap deixa a rolagem suave, e a página andaria na frente da
// pessoa. Navegador sem o "instant" dá erro com ele: lá, a rolagem normal
function scrollBack(top) {
  try {
    window.scrollTo({ top, left: 0, behavior: 'instant' });
  } catch (error) {
    window.scrollTo(0, top);
  }
}

// Calendário maximizado da Agenda: o próprio cartão passa a ocupar a janela, com cara de modal (style.css), sem montar outro
// calendário (a visão escolhida e a rolagem dele continuam). Só com enabled (computador): com a janela em até 992 px, volta
// ao normal e não maximiza de novo sozinho. Enquanto maximizado:
// - a página de trás não rola; ao restaurar, ela volta para onde estava (sem o cartão no lugar, ela encolhe e a rolagem vai a 0)
// - o Esc restaura, e o Tab fica dentro do cartão (boxRef)
export function useMaximized(boxRef, enabled) {
  const [maximized, setMaximized] = useState(false);
  const pageScroll = useRef(null);

  useLayoutEffect(() => {
    if (enabled) return;
    pageScroll.current = null; // no celular a tela é outra: a rolagem de antes não vale
    setMaximized(false);
  }, [enabled]);

  useLayoutEffect(() => {
    if (!maximized) {
      if (pageScroll.current !== null) scrollBack(pageScroll.current);
      pageScroll.current = null;
      return undefined;
    }

    const page = document.documentElement;
    page.classList.add(MAXIMIZED_PAGE_CLASS);
    // Na captura: antes do Esc do modal do react-bootstrap (no document) e da pergunta do SweetAlert (na janela dela), que
    // fecham a janela de cima e a tiram da tela. Ouvindo depois, o Esc que fechou a janela restauraria o calendário também
    const onKeyDown = (event) => {
      if ((event.key !== 'Escape' && event.key !== 'Tab') || document.querySelector(OVERLAY)) return;
      if (event.key === 'Escape') {
        setMaximized(false);
        return;
      }

      // Tab: do último item do cartão vai para o primeiro (Shift+Tab, o contrário); com o foco atrás do cartão, volta a ele
      const box = boxRef.current;
      const items = box ? [...box.querySelectorAll(FOCUSABLE)].filter(isVisible) : [];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      let next = null;
      if (!box.contains(current)) next = event.shiftKey ? last : first;
      else if (event.shiftKey && current === first) next = last;
      else if (!event.shiftKey && current === last) next = first;
      if (!next) return;
      event.preventDefault();
      next.focus();
    };
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      page.classList.remove(MAXIMIZED_PAGE_CLASS);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [maximized, boxRef]);

  // O mesmo botão maximiza e restaura
  function toggle() {
    if (!maximized) pageScroll.current = window.scrollY;
    setMaximized(!maximized);
  }

  return [maximized && enabled, toggle];
}
